/**
 * Analytics Service
 * Handles API calls for QR code scan analytics
 */

import { getRedirectUrl, getPreviewUrl } from '../utils/urlHelper';

import api from './api';

// Types
export interface Scan {
  id: number;
  qrcode_id: number;
  ip_address: string | null;
  user_agent: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  country: string | null;
  city: string | null;
  latitude: number | null;
  longitude: number | null;
  referrer: string | null;
  scanned_at: string;
}

export interface PaginatedScans {
  scans: Scan[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface Statistics {
  total_scans: number;
  unique_visitors: number;
  countries_count: number;
  cities_count: number;
}

export interface DeviceTypeData {
  device_type: string;
  count: number;
}

export interface BrowserData {
  browser: string;
  count: number;
}

export interface OSData {
  os: string;
  count: number;
}

export interface CountryData {
  country: string;
  count: number;
}

export interface CityData {
  city: string;
  country: string;
  count: number;
}

export interface TimelineData {
  date: string;
  count: number;
}

export interface HourlyData {
  hour: number;
  count: number;
}

export interface WeeklyData {
  day: string;
  day_number: number;
  count: number;
}

export interface ComprehensiveAnalytics {
  qrcode: {
    id: number;
    name: string;
    description?: string;
    destination_url: string;
    shortcode: string;
    size: number;
    color_dark: string;
    color_light: string;
    error_correction: 'L' | 'M' | 'Q' | 'H';
    module_style: 'square' | 'rounded' | 'dots' | 'extra-rounded';
    margin: number;
    created_at: string;
  };
  analytics: {
    statistics: Statistics;
    device_types: DeviceTypeData[];
    browsers: BrowserData[];
    operating_systems: OSData[];
    countries: CountryData[];
    cities: CityData[];
    scans_over_time: TimelineData[];
    scans_by_hour: HourlyData[];
    scans_by_day: WeeklyData[];
  };
}

export interface LocationAnalytics {
  countries: CountryData[];
  cities: CityData[];
}

export interface TimelineAnalytics {
  daily: TimelineData[];
  hourly: HourlyData[];
  weekly: WeeklyData[];
}

// Service methods
class AnalyticsService {
  /**
   * Get comprehensive analytics for a QR code
   */
  async getComprehensiveAnalytics(qrcodeId: number): Promise<ComprehensiveAnalytics> {
    const response = await api.get(`/analytics/${qrcodeId}`);
    return response.data.data;
  }

  /**
   * Get all scans for a QR code with pagination
   */
  async getScans(
    qrcodeId: number,
    options: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'ASC' | 'DESC';
    } = {}
  ): Promise<PaginatedScans> {
    const { page = 1, limit = 50, sortBy = 'scanned_at', sortOrder = 'DESC' } = options;

    const response = await api.get(`/analytics/${qrcodeId}/scans`, {
      params: { page, limit, sortBy, sortOrder },
    });

    return {
      scans: response.data.data,
      pagination: response.data.pagination,
    };
  }

  /**
   * Get recent scans for a QR code
   */
  async getRecentScans(qrcodeId: number, limit: number = 10): Promise<Scan[]> {
    const response = await api.get(`/analytics/${qrcodeId}/recent`, {
      params: { limit },
    });
    return response.data.data;
  }

  /**
   * Get device analytics for a QR code
   */
  async getDeviceAnalytics(qrcodeId: number): Promise<DeviceTypeData[]> {
    const response = await api.get(`/analytics/${qrcodeId}/devices`);
    return response.data.data;
  }

  /**
   * Get location analytics for a QR code
   */
  async getLocationAnalytics(qrcodeId: number): Promise<LocationAnalytics> {
    const response = await api.get(`/analytics/${qrcodeId}/locations`);
    return response.data.data;
  }

  /**
   * Get timeline analytics for a QR code
   */
  async getTimelineAnalytics(qrcodeId: number, days: number = 30): Promise<TimelineAnalytics> {
    const response = await api.get(`/analytics/${qrcodeId}/timeline`, {
      params: { days },
    });
    return response.data.data;
  }

  /**
   * Export scans data as CSV or JSON
   */
  async exportScans(qrcodeId: number, format: 'csv' | 'json' = 'csv'): Promise<void> {
    const response = await api.get(`/analytics/${qrcodeId}/export`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });

    if (format === 'csv') {
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qrcode-scans-${qrcodeId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      // JSON format - create and download
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qrcode-scans-${qrcodeId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }

  /**
   * Get QR code redirect URL (for sharing)
   * Uses centralized URL helper for consistent production URLs
   */
  getRedirectURL(shortcode: string): string {
    return getRedirectUrl(shortcode);
  }

  /**
   * Get QR code preview URL (without tracking)
   * Uses centralized URL helper for consistent production URLs
   */
  getPreviewURL(shortcode: string): string {
    return getPreviewUrl(shortcode);
  }
}

export default new AnalyticsService();
